// Referral attribution: capture ?ref= codes and persist for 90 days
import api from '@/lib/api';

const KEY = 'veluro_ref';
const ASSET_KEY = 'veluro_ref_asset';
const CAMPAIGN_KEY = 'veluro_ref_campaign';
const GCLID_KEY = 'veluro_gclid';
const COOKIE_DAYS = 90;

function setCookie(value) {
  document.cookie = `${KEY}=${encodeURIComponent(value)}; max-age=${COOKIE_DAYS * 86400}; path=/; SameSite=Lax`;
}

function getCookie() {
  const match = document.cookie.match(new RegExp(`(?:^|; )${KEY}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

// ── Google Ads click id (gclid) — captured for offline conversion import ──
export function captureGclidFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    // gclid is the standard click id; gbraid/wbraid are the iOS app/web equivalents
    const gclid = (params.get('gclid') || params.get('gbraid') || params.get('wbraid') || '').trim();
    if (gclid) {
      const rec = JSON.stringify({ v: gclid.slice(0, 200), t: Date.now() });
      try { localStorage.setItem(GCLID_KEY, rec); } catch {}
      document.cookie = `${GCLID_KEY}=${encodeURIComponent(gclid.slice(0, 200))}; max-age=${COOKIE_DAYS * 86400}; path=/; SameSite=Lax`;
      return gclid;
    }
  } catch {}
  return getGclid();
}

export function getGclid() {
  try {
    const raw = localStorage.getItem(GCLID_KEY);
    if (raw) {
      const rec = JSON.parse(raw);
      // gclid is valid for offline import up to 90 days
      if (rec && rec.v && (Date.now() - (rec.t || 0)) < COOKIE_DAYS * 86400 * 1000) return rec.v;
    }
  } catch {}
  const m = document.cookie.match(new RegExp(`(?:^|; )${GCLID_KEY}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}


export function getRefCode() {
  return getCookie() || localStorage.getItem(KEY) || sessionStorage.getItem(KEY) || null;
}

export function getRefAsset() {
  return localStorage.getItem(ASSET_KEY) || sessionStorage.getItem(ASSET_KEY) || null;
}

export function getRefCampaign() {
  return localStorage.getItem(CAMPAIGN_KEY) || sessionStorage.getItem(CAMPAIGN_KEY) || null;
}

export function captureRefFromUrl() {
  const gclid = captureGclidFromUrl();
  const params = new URLSearchParams(window.location.search);
  const ref = params.get('ref');
  if (!ref) return getRefCode();
  const code = ref.trim().toUpperCase();
  setCookie(code);
  try { localStorage.setItem(KEY, code); } catch {}
  try { sessionStorage.setItem(KEY, code); } catch {}
  const asset = (params.get('asset') || '').trim();
  if (asset) {
    try { localStorage.setItem(ASSET_KEY, asset); } catch {}
    try { sessionStorage.setItem(ASSET_KEY, asset); } catch {}
  }
  // Campaign sub-ID (?c=hvac) — lets resellers attribute each signup to a specific campaign
  const campaign = (params.get('c') || params.get('utm_campaign') || '').trim().slice(0, 64);
  if (campaign) {
    try { localStorage.setItem(CAMPAIGN_KEY, campaign); } catch {}
    try { sessionStorage.setItem(CAMPAIGN_KEY, campaign); } catch {}
  }
  // Fire-and-forget click tracking (once per session per code)
  const trackedKey = `${KEY}_tracked_${code}_${campaign}`;
  if (!sessionStorage.getItem(trackedKey)) {
    try { sessionStorage.setItem(trackedKey, '1'); } catch {}
    api.post('/referrals/track', { ref_code: code, landing_path: window.location.pathname, asset: asset || getRefAsset(), campaign: campaign || getRefCampaign(), gclid: gclid || getGclid() }).catch(() => {});
  }
  return code;
}

export async function getReferrerInfo() {
  const code = getRefCode();
  if (!code) return null;
  try {
    const res = await api.get(`/referrals/partner-info/${code}`);
    return res.data.valid ? { code, ...res.data } : null;
  } catch {
    return null;
  }
}

export async function initResellerPixel() {
  const code = getRefCode();
  if (!code || window.__veluroResellerPixel) return;
  window.__veluroResellerPixel = true;
  try {
    const res = await api.get(`/referrals/pixel/${code}`);
    if (!res.data.found || res.data.pixel_type !== 'meta') return;
    const raw = String(res.data.pixel_code || '');
    const pixelId = (raw.match(/\d{8,}/) || [null])[0];
    if (!pixelId) return;
    if (!window.fbq) {
      /* eslint-disable */
      !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
      /* eslint-enable */
    }
    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');
    api.post('/referrals/pixel-event', { ref_code: code, event: 'PageView', path: window.location.pathname }).catch(() => {});
  } catch { /* pixel is best-effort */ }
}

export function fireResellerLead() {
  try {
    const code = getRefCode();
    if (code && window.fbq) {
      window.fbq('track', 'Lead');
      api.post('/referrals/pixel-event', { ref_code: code, event: 'Lead', path: window.location.pathname }).catch(() => {});
    }
  } catch { /* best-effort */ }
}
